import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { BRAND } from '@tarot/shared';

/**
 * 마지막 안전망 — 렌더 중 예외가 나도 **빈 화면을 남기지 않는다.**
 *
 * 이게 없으면 React가 트리를 통째로 언마운트해서 배경색만 남는다. 브라우저에서는
 * 새로고침이라도 하지만, 토스 미니앱 웹뷰에는 주소창도 새로고침 버튼도 없어
 * 사용자가 앱을 완전히 빠져나가는 것 말고 할 수 있는 일이 없다
 * (2026-08-28 실기기에서 실제로 그렇게 멈췄다).
 *
 * 오류 자체를 없애 주지는 않는다. 다만 **빠져나갈 길**은 항상 있어야 한다.
 */
interface Props {
  children: ReactNode;
}
interface State {
  failed: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // 바운더리가 삼킨 예외는 전역 핸들러에 안 걸린다 — 여기서 직접 올린다
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
    console.error('화면 렌더 실패:', error);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="text-lg font-bold text-moon">화면을 그리지 못했어요</h1>
        <p className="text-sm leading-relaxed text-moon-dim">
          예상치 못한 오류가 났어요. 다시 시도해 주세요.
          <br />
          계속 이러면 {BRAND.supportEmail}로 알려주시면 고칠게요.
        </p>
        <div className="flex w-full max-w-xs flex-col gap-2">
          <button
            onClick={() => location.reload()}
            className="h-12 rounded-2xl bg-gold px-5 text-sm font-semibold text-night transition hover:bg-gold-deep"
          >
            다시 시도
          </button>
          {/*
            홈으로는 **주소를 갈아 끼워** 보낸다. 라우터를 쓰면 방금 깨진 트리를
            그대로 다시 그릴 수 있어서, 같은 자리에서 또 멈춘다.
          */}
          <button
            onClick={() => location.assign('/')}
            className="h-12 rounded-2xl border border-white/20 px-5 text-sm font-semibold text-moon transition hover:bg-white/5"
          >
            처음으로
          </button>
        </div>
      </div>
    );
  }
}
